import { TpmsSensor, TpmsVehicleType } from 'src/models';

export function barToPsi(bar: number): number {
  return bar * 14.5038;
}

export function celsiusToFahrenheit(celsius: number): number {
  return (celsius * 9) / 5 + 32;
}

export type TpmsLeakState = 'none' | 'emergency' | 'slow' | 'inflating';

export type TpmsStatusFlags = {
  lowBattery: boolean;
  noRecentData: boolean;
  autoPositioning: boolean;
  highPressure: boolean;
  lowPressure: boolean;
  highTemperature: boolean;
  leak: TpmsLeakState;
};

const LEAK_STATES: TpmsLeakState[] = ['none', 'emergency', 'slow', 'inflating'];

export function decodeTpmsStatus(status: number): TpmsStatusFlags {
  /* eslint-disable no-bitwise */
  return {
    lowBattery: !!(status & 0x80),
    noRecentData: !!(status & 0x40),
    autoPositioning: !!(status & 0x20),
    highPressure: !!(status & 0x10),
    lowPressure: !!(status & 0x08),
    highTemperature: !!(status & 0x04),
    leak: LEAK_STATES[status & 0x03],
  };
  /* eslint-enable no-bitwise */
}

// admin-created tpms devices carry Go's zero time until their first report
export function hasTpmsReported(lastReportedAt: string): boolean {
  return new Date(lastReportedAt).getFullYear() >= 2000;
}

const STALE_AFTER_MS = 24 * 60 * 60 * 1000;

export function isSensorStale(sensor: TpmsSensor, now: number = Date.now()): boolean {
  if (decodeTpmsStatus(sensor.status).noRecentData) {
    return true;
  }
  return now - new Date(sensor.lastReportedAt).getTime() > STALE_AFTER_MS;
}

export type TpmsSeverity = 'stale' | 'alert' | 'warn' | 'ok';

export function sensorSeverity(sensor: TpmsSensor): TpmsSeverity {
  if (isSensorStale(sensor)) {
    return 'stale';
  }
  const flags = decodeTpmsStatus(sensor.status);
  if (flags.lowPressure || flags.highPressure || flags.highTemperature || flags.leak === 'emergency') {
    return 'alert';
  }
  if (flags.leak === 'slow' || flags.leak === 'inflating' || flags.lowBattery) {
    return 'warn';
  }
  return 'ok';
}

export function deviceAlertCount(sensors: TpmsSensor[]): number {
  return sensors.filter((sensor) => {
    const severity = sensorSeverity(sensor);
    return severity === 'alert' || severity === 'warn';
  }).length;
}

// reported position byte: bits 7-5 trailer number, bits 4-0 tire serial
export function formatReportedPosition(reportedPosition: number): string {
  // eslint-disable-next-line no-bitwise
  return `${reportedPosition & 0x1f}`;
}

export type WheelSlotDef = {
  index: number;
  label: string;
};

export const TRUCK_SLOTS: WheelSlotDef[] = [
  { index: 0, label: 'LF' },
  { index: 1, label: 'RF' },
  { index: 2, label: 'L1O' },
  { index: 3, label: 'L1I' },
  { index: 4, label: 'R1I' },
  { index: 5, label: 'R1O' },
  { index: 6, label: 'L2O' },
  { index: 7, label: 'L2I' },
  { index: 8, label: 'R2I' },
  { index: 9, label: 'R2O' },
];

export const TRAILER_SLOTS: WheelSlotDef[] = [
  { index: 0, label: 'L1O' },
  { index: 1, label: 'L1I' },
  { index: 2, label: 'R1I' },
  { index: 3, label: 'R1O' },
  { index: 4, label: 'L2O' },
  { index: 5, label: 'L2I' },
  { index: 6, label: 'R2I' },
  { index: 7, label: 'R2O' },
];

export function slotsForVehicleType(vehicleType: TpmsVehicleType): WheelSlotDef[] {
  return vehicleType === 'trailer' ? TRAILER_SLOTS : TRUCK_SLOTS;
}

// axle rows of slot indexes, each row split into a left and right half around the center gap
export function slotRowsForVehicleType(vehicleType: TpmsVehicleType): number[][] {
  if (vehicleType === 'trailer') {
    return [
      [0, 1, 2, 3],
      [4, 5, 6, 7],
    ];
  }
  return [
    [0, 1],
    [2, 3, 4, 5],
    [6, 7, 8, 9],
  ];
}
